// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import AbstractState from '../../animationpack/state/AbstractState';
import BinaryLerpSource from '../../animationpack/blendtree/BinaryLerpSource';
import MathUtils from '../../math/MathUtils';

/**
 * An abstract transition state that blends between two other states.
 */
class AbstractTransitionState extends AbstractState {
  constructor() {
    super();

    this._sourceState = null;
    this._targetState = null;
    this._percent = 0.0;
    this._sourceData = null;
    this._fadeTime = 0;
    this._blendType = AbstractTransitionState.BLENDTYPES.LINEAR;
  }

  getCurrentLoop() {
    return 0;
  }

  /**
   * Update this state using the current global time.
   * @param {number} globalTime the current global time.
   */
  // Was: function (globalTime, layer)
  update(globalTime) {
    const currentTime = globalTime - this._globalStartTime;
    if (currentTime > this._fadeTime && this.onFinished) {
      this.onFinished();
      return;
    }
    const percent = currentTime / this._fadeTime;
    switch (this._blendType) {
      case AbstractTransitionState.BLENDTYPES.CUBIC:
        this._percent = MathUtils.scurve3(percent);
        break;
      case AbstractTransitionState.BLENDTYPES.QUINTIC:
        this._percent = MathUtils.scurve5(percent);
        break;
      default:
        this._percent = percent;
    }
  }

  /**
   * Method for setting fade time and blend type from config data.
   * @param {Object} config configuration data
   */

  readFromConfig(config) {
    if (config) {
      if (config.fadeTime !== undefined) { this._fadeTime = config.fadeTime; }
      if (config.blendType !== undefined) { this._blendType = config.blendType; }
    }
  }

  /**
   * @returns the current map of source channel data for this layer.
   */
  getCurrentSourceData() {
    // grab our data maps from the two states
    const sourceAData = this._sourceState ? this._sourceState.getCurrentSourceData() : null;
    const sourceBData = this._targetState ? this._targetState.getCurrentSourceData() : null;

    // reuse previous _sourceData transforms to avoid re-creating
    // too many new transform data objects. This assumes that a
    // same state always returns the same transform data objects.
    if (!this._sourceData) {
      this._sourceData = {};
    }
    return BinaryLerpSource.combineSourceData(sourceAData, sourceBData, this._percent, this._sourceData);
  }

  /**
   * Check if a transition is valid within a given time window.
   *
   * @param {Array} timeWindow start and end time
   * @param {number} globalTime current world time
   * @returns {boolean} true if transition is valid
   */

  isValid(timeWindow, globalTime) {
    const localTime = globalTime - this._sourceState._globalStartTime;
    const start = timeWindow[0];
    const end = timeWindow[1];

    if (start <= 0) {
      if (end <= 0) {
        // no window, so true
        return true;
      }
        // just check end
      return localTime <= end;
    }
    if (end <= 0) {
        // just check start
      return localTime >= start;
    } else if (start <= end) {
        // check between start and end
      return start <= localTime && localTime <= end;
    }
        // start is greater than end, so there are two windows.
    return localTime >= start || localTime <= end;
  }

  resetClips(globalTime) {
    super.resetClips(globalTime);
    // this._sourceData = {};
    this._percent = 0.0;
  }

  shiftClipTime(shiftTime) {
    super.shiftClipTime(shiftTime);
    // this._percent = 0.0;  // definitely not 0, or maybe 0
  }

  setTimeScale(timeScale) {
    if (this._sourceState) {
      this._sourceState.setTimeScale(timeScale);
    }
    if (this._targetState) {
      this._targetState.setTimeScale(timeScale);
    }
  }
}

AbstractTransitionState.BLENDTYPES = {};
AbstractTransitionState.BLENDTYPES.LINEAR = 'Linear';
AbstractTransitionState.BLENDTYPES.CUBIC = 'SCurve3';
AbstractTransitionState.BLENDTYPES.QUINTIC = 'SCurve5';

export default AbstractTransitionState;
